// NotificationService.js

/**
 * NotificationService.js
 * 
 * Service to manage user notifications in Firestore.
 */

// Firebase
import { Timestamp, collection, deleteDoc, doc, limit as limitTo, onSnapshot, orderBy, query, setDoc, updateDoc, where, writeBatch } from 'firebase/firestore';
import { collections, db } from 'firebaseConfig';

// Utilities
import { NotificationPriority, NotificationSourceType, NotificationType } from 'utilities/Enums';
import { generateKey } from 'utilities/Keys';
import { getPersonName } from 'utilities/PersonIdentity';
import { withBaseKey } from 'utilities/basePersistence';
import { isIgnoredActivitySummaryText } from 'node/activity/utils/placeholderTitleRename';
import { formatActivityReactionNotificationTitle } from 'node/activity/utils/activityReactions';

/**
 * Rows fetched per page of the base notifications feed.
 */
export const NOTIFICATION_PAGE_SIZE = 40;

/**
 * Ceiling on the always-on unread feed that drives badge counts. Badges only need
 * "how many, roughly", so this is bounded rather than complete.
 */
export const UNREAD_NOTIFICATIONS_LIMIT = 200;

// Firestore rejects batches over 500 writes.
const WRITE_BATCH_SIZE = 400;

// Helper to check for non-empty string
const isNonEmptyString = (value) => typeof value === 'string' && value.length > 0;

/**
 * Hide bookkeeping-only NODE_CHANGE rows already written (e.g. accessScope backfill).
 *
 * @param {object|null|undefined} notification
 * @returns {boolean}
 */
export function shouldIncludeNotification(notification) {
    const subtitle = String(notification?.subtitle || '').trim();
    if (!subtitle) return true;

    const withoutMore = subtitle.replace(/\s*\+\d+\s+more\.?$/i, '').trim();
    const parts = withoutMore
        .split(/(?<=\.)\s+/)
        .map((entry) => String(entry || '').trim())
        .filter(Boolean);

    if (parts.length === 0) return true;
    return parts.some((part) => !isIgnoredActivitySummaryText(part));
}

/**
 * Splits keys into batch-sized chunks.
 *
 * @param {Array<string>} keys - Document keys.
 * @returns {Array<Array<string>>} Chunks no larger than the batch limit.
 */
const chunkKeys = (keys) => {
    const chunks = [];
    for (let index = 0; index < keys.length; index += WRITE_BATCH_SIZE) {
        chunks.push(keys.slice(index, index + WRITE_BATCH_SIZE));
    }
    return chunks;
};

const normalizeBaseKey = (value) => {
    const key = String(value || '').trim();
    return key || null;
};

const resolveBaseKey = ({ baseKey, metadata, actions } = {}) => {
    const topLevelBaseKey = normalizeBaseKey(baseKey);
    if (topLevelBaseKey) return topLevelBaseKey;

    const metadataBaseKey = normalizeBaseKey(metadata?.baseKey || metadata?.baseId);
    if (metadataBaseKey) return metadataBaseKey;

    if (Array.isArray(actions)) {
        for (const action of actions) {
            const actionBaseKey = normalizeBaseKey(
                action?.data?.baseKey || action?.data?.baseId
            );
            if (actionBaseKey) return actionBaseKey;
        }
    }

    return null;
};

/**
 * Marks a notification as read.
 * Also updates dateModified to now.
 * 
 * @param {string} notificationKey - The key of the notification to mark as read.
 */
export async function markNotificationAsRead(notificationKey) {
    // Validate input
    if (!isNonEmptyString(notificationKey)) return;

    // Update the notification document
    const ref = doc(db, collections.notifications, notificationKey);
    await updateDoc(ref, {
        isRead: true,
        dateModified: Timestamp.now(),
    });

}

/**
 * Marks multiple notifications as read.
 * Accepts an array of keys and/or notification objects.
 * Also updates dateModified to now.
 * 
 * @param {Array<string|object>} notificationKeys - Array of notification keys or notification objects.
 */
export async function markNotificationsAsRead(notificationKeys) {

    // Validate input
    if (!Array.isArray(notificationKeys) || notificationKeys.length === 0) return;

    // Extract keys from objects if necessary
    const keys = notificationKeys.filter(isNonEmptyString);

    // Return early if no valid keys
    if (keys.length === 0) return;

    // Remove duplicates
    const uniqueKeys = Array.from(new Set(keys));

    const now = Timestamp.now();

    for (const chunk of chunkKeys(uniqueKeys)) {
        const batch = writeBatch(db);

        chunk.forEach((key) => {
            batch.update(doc(db, collections.notifications, key), {
                isRead: true,
                dateModified: now,
            });
        });

        await batch.commit();
    }
}

/**
 * Convenience helper: marks only unread notifications as read.
 * 
 * @param {Array<object>} notifications - Array of notification objects.
 */
export async function markUnreadNotificationsAsRead(notifications) {
    // Validate input
    if (!Array.isArray(notifications) || notifications.length === 0) return;

    // Filter for unread notifications
    const unread = notifications.filter((n) => n && n.isRead !== true);

    // Return early
    if (unread.length === 0) return;

    // Mark them as read
    return markNotificationsAsRead(unread.map((n) => n?.key).filter(isNonEmptyString));
}

/**
 * Subscribes to a user's unread notifications, newest first, capped.
 *
 * This is the always-on feed behind badge counts. Read rows never enter it, so a user
 * with years of history still streams only what is actually unread.
 *
 * @param {string} userKey - The user key to subscribe notifications for.
 * @param {function} onUpdate - Callback invoked with an array of unread notifications.
 * @param {function} [onError] - Optional error callback.
 *
 * @returns {function} Unsubscribe function to stop listening.
 */
export function subscribeToUnreadUserNotifications(userKey, onUpdate, onError) {

    // Validate input
    if (!userKey) {
        onUpdate?.([]);
        return () => { };
    }

    const q = query(
        collection(db, collections.notifications),
        where('userKey', '==', userKey),
        where('isRead', '==', false),
        orderBy('dateCreated', 'desc'),
        limitTo(UNREAD_NOTIFICATIONS_LIMIT)
    );

    return onSnapshot(
        q,
        (snapshot) => {
            const rows = snapshot.docs
                .map((d) => ({ id: d.id, ...d.data() }))
                .filter(shouldIncludeNotification);
            onUpdate?.(rows);
        },
        (error) => {
            if (onError) return onError(error);
            console.error('Error fetching unread notifications:', error);
        }
    );
}

/**
 * Subscribes to one base's notifications for a user, newest first, within a
 * growing window.
 *
 * The window is the listener's own `limit`, so widening it keeps a single listener and
 * a single ordered result set — there is no separate history array to splice against a
 * live tail. One extra row is requested to detect more without a second read.
 *
 * @param {object} params - Subscription params.
 * @param {string} params.userKey - Owner of the notifications.
 * @param {string} params.baseKey - Base to scope to.
 * @param {number} [params.windowSize] - Rows to load, defaults to one page.
 * @param {function} onUpdate - Called with `{ notifications, hasMore }`.
 * @param {function} [onError] - Optional error callback.
 *
 * @returns {function} Unsubscribe function to stop listening.
 */
export function subscribeToBaseNotifications(
    { userKey, baseKey, windowSize = NOTIFICATION_PAGE_SIZE } = {},
    onUpdate,
    onError
) {

    const cleanUserKey = String(userKey || '').trim();
    const cleanBaseKey = String(baseKey || '').trim();

    if (!cleanUserKey || !cleanBaseKey) {
        onUpdate?.({ notifications: [], hasMore: false });
        return () => { };
    }

    const size = Math.max(1, Number(windowSize) || NOTIFICATION_PAGE_SIZE);

    const q = query(
        collection(db, collections.notifications),
        where('userKey', '==', cleanUserKey),
        where('baseKey', '==', cleanBaseKey),
        orderBy('dateCreated', 'desc'),
        limitTo(size + 1)
    );

    return onSnapshot(
        q,
        (snapshot) => {
            const rows = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
            const hasMore = rows.length > size;
            const window = hasMore ? rows.slice(0, size) : rows;

            onUpdate?.({
                notifications: window.filter(shouldIncludeNotification),
                hasMore,
            });
        },
        (error) => {
            if (onError) return onError(error);
            console.error('Error fetching base notifications:', error);
        }
    );
}

/**
 * Creates a new notification document in the notifications collection.
 * Returns the created notification key.
 * 
 * @param {object} params - Notification parameters.
 * @param {string} params.userKey - The user key to whom the notification is addressed.
 * @param {string} params.type - The type of notification (use NotificationType enum).
 * @param {string} params.title - The title/text of the notification.
 * @param {string|null} [params.subtitle=null] - Optional subtitle text.
 * @param {string} [params.sourceType=NotificationSourceType.ENGAGEMENT] - Source type of the notification.
 * @param {string|null} [params.sourceKey=null] - Optional source key related to the notification.
 * @param {string|null} [params.baseKey=null] - Optional base key associated with this notification.
 * @param {object} [params.metadata={}] - Optional metadata object for additional info.
 * @param {string} [params.priority=NotificationPriority.NORMAL] - Priority level of the notification.
 * @param {Array<object>} [params.actions=[]] - Optional array of action objects for the notification.
 * 
 * @returns {Promise<string>} The key of the created notification document.
 */
export async function createNotification({
    userKey,
    type,
    title,
    subtitle = null,
    sourceType = NotificationSourceType.ENGAGEMENT,
    sourceKey = null,
    baseKey,
    metadata = {},
    priority = NotificationPriority.NORMAL,
    actions = [],
} = {}) {
    // Generate a new notification key
    const key = generateKey();

    const normalizedMetadata = metadata && typeof metadata === 'object' ? { ...metadata } : {};
    const normalizedActions = Array.isArray(actions) ? actions : [];

    const resolvedBaseKey = resolveBaseKey({
        baseKey,
        metadata: normalizedMetadata,
        actions: normalizedActions,
    });

    if (resolvedBaseKey && !normalizeBaseKey(normalizedMetadata.baseKey)) {
        normalizedMetadata.baseKey = resolvedBaseKey;
    }

    // Prepare the notification object
    const now = Timestamp.now();
    const notification = withBaseKey({
        key,
        userKey,
        baseKey: resolvedBaseKey,
        type,
        title,
        subtitle,
        sourceType,
        sourceKey,
        metadata: normalizedMetadata,
        priority,
        actions: normalizedActions,
        isRead: false,
        dateCreated: now,
        dateModified: now,
    }, resolvedBaseKey);

    // Create the notification document
    await setDoc(doc(db, collections.notifications, key), notification);

    // Return the created notification key
    return key;
}

/**
 * Deletes a notification document.
 * 
 * @param {string} notificationKey - The key of the notification to delete.
 */
export async function deleteNotification(notificationKey) {

    // Validate input
    if (!isNonEmptyString(notificationKey)) return;

    // Delete the notification document
    await deleteDoc(doc(db, collections.notifications, notificationKey));
}

/**
 * Deletes multiple notification documents.
 * Accepts an array of keys and/or notification objects.
 *
 * @param {Array<string|object>} notificationKeys - Array of notification keys or notification objects.
 */
export async function deleteNotifications(notificationKeys) {
    if (!Array.isArray(notificationKeys) || notificationKeys.length === 0) return;

    const keys = notificationKeys
        .map((entry) => (typeof entry === 'string' ? entry : entry?.key))
        .filter(isNonEmptyString);

    if (keys.length === 0) return;

    const uniqueKeys = Array.from(new Set(keys));

    for (const chunk of chunkKeys(uniqueKeys)) {
        const batch = writeBatch(db);

        chunk.forEach((key) => {
            batch.delete(doc(db, collections.notifications, key));
        });

        await batch.commit();
    }
}

/**
 * Creates a notification from an engagement record.
 * Returns the created notification key, or null if it should be skipped.
 * 
 * @param {object} engagement - The engagement record.
 * @param {object} actor - The user who performed the engagement.
 * @param {object} targetUser - The user to notify.
 * @param {object|null} [itemSummary=null] - Optional summary of the item involved in the engagement.
 * 
 * @returns {Promise<string|null>} The key of the created notification, or null if skipped.
 */
export async function createNotificationFromEngagement(engagement, actor, targetUser, itemSummary = null) {

    // Validate input
    const { type, text, key: sourceKey } = engagement || {};

    // Skip notifications when the actor and target are the same user
    if (!actor?.key || !targetUser?.key || actor.key === targetUser.key) {
        return null;
    }

    let notificationType;
    let title;
    const actorDisplayName = getPersonName(actor, 'Someone');

    // Determine notification type and title based on engagement type
    switch (type) {
        case 'NODE_LIKE':
            notificationType = NotificationType.ENGAGEMENT_LIKE;
            title = `${actorDisplayName} liked your post`;
            break;
        case 'NODE_COMMENT':
            notificationType = NotificationType.ENGAGEMENT_COMMENT;
            title = `${actorDisplayName} commented: ${text}`;
            break;
        case 'NODE_FAVORITE':
            notificationType = NotificationType.ENGAGEMENT_FAVORITE;
            title = `${actorDisplayName} added your post to favorites`;
            break;
        case 'NODE_SHARE':
            notificationType = NotificationType.ENGAGEMENT_SHARE;
            title = `${actorDisplayName} shared your post`;
            break;
        case 'CONTRIBUTION_MADE':
            notificationType = NotificationType.ENGAGEMENT_CONTRIBUTION;
            title = `${actorDisplayName} contributed to your post`;
            break;
        case 'SESSION_SET':
            notificationType = NotificationType.ENGAGEMENT_SESSION;
            title = `${actorDisplayName} booked an appointment`;
            break;
        case 'USER_VIEW':
            notificationType = NotificationType.ENGAGEMENT_USER_VIEW;
            title = `${actorDisplayName} viewed your profile`;
            break;
        case 'ACTIVITY_REACTION':
            notificationType = NotificationType.ENGAGEMENT_LIKE;
            title = formatActivityReactionNotificationTitle(
                actorDisplayName,
                engagement.reaction
            );
            break;
        default:
            // Skip unknown engagement types
            return null;
    }

    // Prepare metadata
    const baseKey = normalizeBaseKey(
        engagement?.baseKey
        || engagement?.baseId
        || engagement?.itemSummary?.baseKey
        || engagement?.itemSummary?.baseId
        || engagement?.itemSummary?.base?.key
        || itemSummary?.baseKey
        || itemSummary?.baseId
        || itemSummary?.base?.key
    );

    const metadata = {
        actorKey: actor.key,
        actorName: actorDisplayName,
        itemKey: itemSummary?.key,
        itemTitle: itemSummary?.title,
        engagementText: text,
        ...(baseKey ? { baseKey } : {}),
    };

    // Add navigation action for most types
    const actions = [];
    if (itemSummary?.key) {
        actions.push({
            label: 'View',
            action: 'NAVIGATE',
            data: { section: 'NODE', nodeKey: itemSummary.key },
        });
    }

    // Create the notification document
    return createNotification({
        userKey: targetUser.key,
        type: notificationType,
        title,
        sourceType: NotificationSourceType.ENGAGEMENT,
        sourceKey,
        baseKey,
        metadata,
        actions,
    });
}
